import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Buildings, MapPin, PaperPlaneTilt, Coins, Check, CheckCircle, Clock, XCircle, Info, MusicNote } from '@phosphor-icons/react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const GENRES = ['all', 'electronic', 'indie', 'hip-hop'];

export default function Labels() {
  const [labels, setLabels] = useState([]);
  const [demos, setDemos] = useState([]);
  const [genre, setGenre] = useState('all');
  const [subgenre, setSubgenre] = useState('all');
  const [microgenre, setMicrogenre] = useState('all');
  const [filters, setFilters] = useState({
    genres: [],
    subgenres_by_genre: {},
    microgenres_by_genre: {},
  });
  const [selected, setSelected] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState('manual');
  const [showPitch, setShowPitch] = useState(false);
  const [pitch, setPitch] = useState('');
  const [soundcloudLink, setSoundcloudLink] = useState('');
  const [soundcloudData, setSoundcloudData] = useState(null);
  const [resolvingSoundCloud, setResolvingSoundCloud] = useState(false);
  const { refresh, user } = useAuth();

  const load = useCallback(() => Promise.all([
    api.get('/labels', {
      params: {
        ...(genre !== 'all' ? { genre } : {}),
        ...(subgenre !== 'all' ? { subgenre } : {}),
        ...(microgenre !== 'all' ? { microgenre } : {}),
      },
    }).then(r => setLabels(r.data)),
    api.get('/demos').then(r => setDemos(r.data)),
  ]), [genre, subgenre, microgenre]);

  useEffect(() => {
    Promise.all([
      api.get('/labels/filters').then(r => setFilters(r.data)),
        api.get('/demos').then(r => setDemos(r.data)),
    ]);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const availableSubgenres = useMemo(() => {
    if (genre === 'all') return [];
    return filters.subgenres_by_genre?.[genre] || [];
  }, [filters, genre]);

  const availableMicrogenres = useMemo(() => {
    if (genre === 'all' || subgenre === 'all') return [];
    return filters.microgenres_by_genre?.[genre]?.[subgenre] || [];
  }, [filters, genre, subgenre]);

  const visible = useMemo(() => labels, [labels]);

  const isAllMatching = selectionMode === 'all_matching';
  const costPerLabel = isAllMatching ? 1 : 2;
  const estimatedCost = selected.size * costPerLabel;

  const selectGenre = (value) => {
    setGenre(value);
    setSubgenre('all');
    setMicrogenre('all');
    setSelected(new Set());
    setSelectionMode('manual');
  };

  const selectSubgenre = (value) => {
    setSubgenre(value);
    setMicrogenre('all');
    setSelected(new Set());
    setSelectionMode('manual');
  };

  const selectMicrogenre = (value) => {
    setMicrogenre(value);
    setSelected(new Set());
    setSelectionMode('manual');
  };

  const toggle = (label_id) => {
    setSelectionMode('manual');

    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(label_id)) {
        n.delete(label_id);
      } else {
        n.add(label_id);
      }
      return n;
    });
  };

  const selectAllVisible = () => {
    setSelectionMode('all_matching');
    setSelected(new Set(visible.map(l => l.label_id)));
  };

  const clearAll = () => {
    setSelectionMode('manual');
    setSelected(new Set());
  };


  const resolveSoundCloudDemo = async () => {
    const url = soundcloudLink.trim();

    if (!url) {
      toast.error('Paste a SoundCloud link');
      return;
    }

    if (!url.toLowerCase().includes('soundcloud.com')) {
      toast.error('Please use a SoundCloud link');
      return;
    }

    setResolvingSoundCloud(true);
    setSoundcloudData(null);

    try {
      const { data } = await api.post('/links/resolve', { url });

      setSoundcloudData({
        ...data,
        soundcloud_url: data.soundcloud_url || url,
        genre: data.genre || '',
        subgenre: data.subgenre || '',
        microgenre: data.microgenre || '',
      });

      toast.success(
        data.genre
          ? `Found "${data.title}" by ${data.artist}`
          : `Found "${data.title}" by ${data.artist} · choose a genre`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Couldn't read that SoundCloud link"
      );
    } finally {
      setResolvingSoundCloud(false);
    }
  };

  const submit = async () => {
    if (!soundcloudData?.soundcloud_url) {
      toast.error('Resolve a SoundCloud link first');
      return;
    }

    if (!soundcloudData.genre) {
      toast.error('Choose a genre for this demo');
      return;
    }

    if (selected.size === 0) {
      toast.error('Pick at least one label');
      return;
    }

    try {
      const { data } = await api.post('/demos', {
        soundcloud_url: soundcloudData.soundcloud_url,
        title: soundcloudData.title,
        artist: soundcloudData.artist,
        genre: soundcloudData.genre,
        subgenre: soundcloudData.subgenre || undefined,
        microgenre: soundcloudData.microgenre || undefined,
        soundcloud_genre: soundcloudData.soundcloud_genre || undefined,
        soundcloud_embed_html: soundcloudData.soundcloud_embed_html || undefined,
        cover_url: soundcloudData.cover_url || undefined,
        label_ids: Array.from(selected),
        pitch: pitch || undefined,
        selection_mode: selectionMode,
      });

      toast.success(
        `Sent to ${data.label_ids.length} label(s) · ${data.credits_charged} credits`
      );

      setShowPitch(false);
      setSelected(new Set());
      setSelectionMode('manual');
      setPitch('');
      setSoundcloudLink('');
      setSoundcloudData(null);

      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };


  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Label Demo System</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Pitch to labels.</h1>
          <p className="text-zinc-400 mt-1 text-sm max-w-2xl">
            Browse labels by genre and subgenre. Select individual targets, or select all matching labels for the lower rate.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs font-mono text-[#ccff00] px-3 py-1.5 border border-[#ccff00]/30 rounded-full">
          <Info size={12} /> 1 Credit/label when selecting all matching · 2 for manual targets
        </div>
      </header>

      <Tabs defaultValue="browse" className="tabs-underline">
        <TabsList className="bg-transparent border-b border-zinc-800 rounded-none w-full justify-start h-auto p-0 gap-6 mb-6">
          <TabsTrigger data-testid="tab-browse-labels" value="browse" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">Browse labels</TabsTrigger>
          <TabsTrigger data-testid="tab-my-demos" value="history" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">My demos ({demos.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          {/* filters */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <SearchableFilter
                label="Genre"
                value={genre}
                options={filters.genres.map(g => ({
                  value: g,
                  label: formatTaxonomyLabel(g),
                }))}
                placeholder="All genres"
                onChange={selectGenre}
              />

              <SearchableFilter
                label="Subgenre"
                value={subgenre}
                options={availableSubgenres.map(sg => ({
                  value: sg,
                  label: formatTaxonomyLabel(sg),
                }))}
                placeholder={
                  genre === 'all'
                    ? 'Select genre first'
                    : 'All subgenres'
                }
                disabled={genre === 'all'}
                onChange={selectSubgenre}
              />

              <SearchableFilter
                label="Microgenre"
                value={microgenre}
                options={availableMicrogenres.map(mg => ({
                  value: mg,
                  label: formatTaxonomyLabel(mg),
                }))}
                placeholder={
                  genre === 'all'
                    ? 'Select genre first'
                    : subgenre === 'all'
                      ? 'Select subgenre first'
                      : 'All microgenres'
                }
                disabled={genre === 'all' || subgenre === 'all'}
                onChange={selectMicrogenre}
              />

              <div className="lg:ml-auto flex flex-wrap items-end justify-end gap-x-3 gap-y-2">
                <div className="flex flex-col items-end gap-1 min-w-[230px]">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                      {visible.length} matching label{visible.length !== 1 ? 's' : ''}
                    </span>

                    <button
                      type="button"
                      onClick={clearAll}
                      disabled={selected.size === 0}
                      className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border transition-colors ${
                        selected.size === 0
                          ? 'border-zinc-900 text-zinc-700 cursor-not-allowed'
                          : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                      }`}
                    >
                      Clear
                    </button>
                  </div>

                  <button
                    data-testid="select-all-labels"
                    onClick={selectAllVisible}
                    disabled={visible.length === 0}
                    className={`w-full text-xs font-mono px-3 py-2 rounded-full border ${
                      isAllMatching
                        ? 'border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    } ${
                      visible.length === 0
                        ? 'opacity-40 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <span className="block">Select all matching</span>
                    <span className="block text-[10px] mt-0.5 opacity-80">
                      · 1 Credit/label
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>


          {/* label grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-in mb-24">
            {visible.map(l => {
              const on = selected.has(l.label_id);
              return (
                <div
                  key={l.label_id}
                  data-testid={`label-${l.label_id}`}
                  onClick={() => toggle(l.label_id)}
                  className={`cursor-pointer border rounded-xl p-5 transition-[border-color,background-color] ${
                    on ? 'border-[#ccff00] bg-[#ccff00]/5' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <img src={l.avatar} alt="" className="w-12 h-12 rounded-lg object-cover border border-zinc-800" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Buildings size={14} className="text-zinc-500" />
                        <span className="font-display text-lg leading-tight truncate">{l.name}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs font-mono text-zinc-500">
                        <MapPin size={11} /> {l.location}
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded border flex items-center justify-center shrink-0 transition-[background-color,border-color] ${
                      on ? 'bg-[#ccff00] border-[#ccff00]' : 'border-zinc-700'
                    }`}>
                      {on && <Check size={14} weight="bold" className="text-black" />}
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400 leading-snug mb-3">{l.bio}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {l.tags.slice(0, 3).map(t => (
                      <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-zinc-800 text-zinc-500">#{t}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-end text-xs font-mono border-t border-zinc-800 pt-3">
                    <span className="text-zinc-500">
                      Response ~{(l.response_rate * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sticky submit bar */}
          {selected.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 border border-[#ccff00]/50 rounded-2xl bg-zinc-950/95 backdrop-blur-md shadow-2xl px-4 py-3 flex items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="font-display text-lg" data-testid="selected-count">
                  {selected.size}
                </span>
                <span className="font-mono text-xs text-zinc-500">
                  label{selected.size !== 1 ? 's' : ''} selected
                </span>
              </div>

              <div className="h-8 w-px bg-zinc-800" />

              <div className="text-xs font-mono">
                <div className="text-[#ccff00]">
                  {estimatedCost} Credits
                </div>
                <div className="text-zinc-600">
                  {isAllMatching
                    ? 'All matching · 1 Credit/label'
                    : 'Manual selection · 2 Credits/label'}
                </div>
              </div>

              <Button
                data-testid="open-pitch-btn"
                onClick={() => setShowPitch(true)}
                className="btn-neon rounded-full font-display px-5"
              >
                <PaperPlaneTilt size={14} className="mr-2" weight="fill" />
                Send demo
              </Button>
            </div>
          )}

        </TabsContent>

        <TabsContent value="history" className="space-y-3 mb-8">
          {demos.length === 0 && <div className="text-sm text-zinc-500 py-6">No demos sent yet.</div>}
          {demos.map(d => (
            <div key={d.demo_id} data-testid={`demo-${d.demo_id}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{d.song?.title || 'Untitled'}</div>
                  <div className="text-xs font-mono text-zinc-500">{new Date(d.created_at).toLocaleString()} · sent to {d.label_ids.length} · -{d.credits_charged} credits</div>
                </div>
              </div>
              {d.pitch && <p className="text-sm text-zinc-400 italic border-l-2 border-zinc-800 pl-3 mb-3">"{d.pitch}"</p>}
              <div className="flex flex-wrap gap-2">
                {d.labels?.map(l => {
                  const resp = d.responses?.[l.label_id] || { status: 'pending' };
                  return (
                    <div key={l.label_id} className="flex items-center gap-2 border border-zinc-800 rounded-full pl-1 pr-3 py-1 bg-zinc-950">
                      <img src={l.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                      <span className="text-xs font-medium">{l.name}</span>
                      <ResponsePill status={resp.status} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Pitch dialog */}
      <Dialog
        open={showPitch}
        onOpenChange={open => {
          setShowPitch(open);
          if (!open) {
            setSoundcloudLink('');
            setSoundcloudData(null);
            setResolvingSoundCloud(false);
          }
        }}
      >
        <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden pr-3"
      >
          <DialogHeader>
            <DialogTitle className="font-display">Send demo to {selected.size} label{selected.size !== 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="text-xs font-mono text-zinc-500 bg-zinc-900/60 border border-zinc-800 rounded-md p-3">
            <div className="text-zinc-300 mb-1">
              {selected.size} label{selected.size !== 1 ? 's' : ''} selected
            </div>
            <div>
              Estimated cost:{' '}
              <span className="text-[#ccff00]">{estimatedCost} Credits</span>
            </div>
            <div className="mt-1 text-zinc-600">
              {isAllMatching
                ? 'All matching labels selected · 1 Credit per label'
                : 'Manual selection · 2 Credits per label'}
            </div>
          </div>

          <div className="space-y-4">

            {/* SoundCloud link */}
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                SoundCloud demo
              </label>

              <div className="flex gap-2 mt-2">
                <Input
                  data-testid="soundcloud-demo-link-input"
                  value={soundcloudLink}
                  onChange={e => {
                    setSoundcloudLink(e.target.value);
                    setSoundcloudData(null);
                  }}
                  placeholder="Paste SoundCloud track link..."
                  className="bg-zinc-950 border-zinc-800"
                />

                <Button
                  type="button"
                  data-testid="soundcloud-demo-auto-fill"
                  onClick={resolveSoundCloudDemo}
                  disabled={resolvingSoundCloud || !soundcloudLink.trim()}
                  variant="outline"
                  className="border-zinc-800 hover:border-[#ccff00]/60 shrink-0"
                >
                  {resolvingSoundCloud ? 'Reading…' : 'Auto-fill'}
                </Button>
              </div>

              <p className="mt-1.5 text-[10px] text-zinc-600">
                Unreleased demos only. TuneAmplifier stores the SoundCloud link,
                not the audio file.
              </p>
            </div>

            {soundcloudData && (
              <>
                {/* Auto-filled metadata */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                      Artist
                    </label>
                    <Input
                      value={soundcloudData.artist || ''}
                      readOnly
                      className="mt-2 bg-zinc-900/60 border-zinc-800 text-zinc-300"
                    />
                  </div>

                  <div>
                    <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                      Title
                    </label>
                    <Input
                      value={soundcloudData.title || ''}
                      readOnly
                      className="mt-2 bg-zinc-900/60 border-zinc-800 text-zinc-300"
                    />
                  </div>
                </div>

                {/* Genre */}
                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Genre
                  </label>

                  <Select
                    value={soundcloudData.genre || undefined}
                    onValueChange={value => {
                      setSoundcloudData(prev => ({
                        ...prev,
                        genre: value,
                        subgenre: '',
                        microgenre: '',
                      }));
                    }}
                  >
                    <SelectTrigger
                      data-testid="soundcloud-demo-genre"
                      className={`mt-2 bg-zinc-950 border-zinc-800 ${
                        !soundcloudData.genre ? 'border-amber-500/50' : ''
                      }`}
                    >
                      <SelectValue placeholder="Select genre" />
                    </SelectTrigger>

                    <SelectContent>
                      {filters.genres.map(g => (
                        <SelectItem key={g} value={g}>
                          {formatTaxonomyLabel(g)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {!soundcloudData.genre && (
                    <p className="mt-1.5 text-[10px] text-amber-400">
                      The genre could not be identified automatically. Please select it.
                    </p>
                  )}
                </div>

                {/* Subgenre */}
                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Subgenre <span className="text-zinc-700">(optional)</span>
                  </label>

                  <Select
                    value={soundcloudData.subgenre || undefined}
                    onValueChange={value => {
                      setSoundcloudData(prev => ({
                        ...prev,
                        subgenre: value === '__none__' ? '' : value,
                        microgenre: '',
                      }));
                    }}
                    disabled={!soundcloudData.genre}
                  >
                    <SelectTrigger
                      data-testid="soundcloud-demo-subgenre"
                      className="mt-2 bg-zinc-950 border-zinc-800"
                    >
                      <SelectValue
                        placeholder={
                          soundcloudData.genre
                            ? 'All subgenres / choose one'
                            : 'Select genre first'
                        }
                      />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="__none__">
                        No subgenre
                      </SelectItem>

                      {(filters.subgenres_by_genre?.[soundcloudData.genre] || []).map(sg => (
                        <SelectItem key={sg} value={sg}>
                          {formatTaxonomyLabel(sg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Microgenre */}
                <div>
                  <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                    Microgenre <span className="text-zinc-700">(optional)</span>
                  </label>

                  <Select
                    value={soundcloudData.microgenre || undefined}
                    onValueChange={value => {
                      setSoundcloudData(prev => ({
                        ...prev,
                        microgenre: value === '__none__' ? '' : value,
                      }));
                    }}
                    disabled={!soundcloudData.subgenre}
                  >
                    <SelectTrigger
                      data-testid="soundcloud-demo-microgenre"
                      className="mt-2 bg-zinc-950 border-zinc-800"
                    >
                      <SelectValue
                        placeholder={
                          soundcloudData.subgenre
                            ? 'All microgenres / choose one'
                            : 'Select subgenre first'
                        }
                      />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="__none__">
                        No microgenre
                      </SelectItem>

                      {(
                        filters.microgenres_by_genre?.[soundcloudData.genre]?.[
                          soundcloudData.subgenre
                        ] || []
                      ).map(mg => (
                        <SelectItem key={mg} value={mg}>
                          {formatTaxonomyLabel(mg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* SoundCloud player */}
                <div className="border border-zinc-800 rounded-lg bg-zinc-900/30 p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <MusicNote size={14} className="text-[#ccff00]" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                      Preview
                    </span>
                  </div>

                  {soundcloudData.soundcloud_embed_html ? (
                    <iframe
                      title="SoundCloud demo preview"
                      src={(() => {
                        const match = soundcloudData.soundcloud_embed_html.match(
                          /src=["']([^"']+)["']/i
                        );
                        return match?.[1] || '';
                      })()}
                      width="100%"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay; encrypted-media"
                      className="rounded-md"
                    />
                  ) : (
                    <a
                      href={soundcloudData.soundcloud_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#ccff00] hover:underline"
                    >
                      Open track on SoundCloud
                    </a>
                  )}
                </div>
              </>
            )}

            {/* Pitch */}
            <div>
              <label className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
                Pitch (optional)
              </label>
              <Textarea
                data-testid="demo-pitch-input"
                value={pitch}
                onChange={e => setPitch(e.target.value)}
                placeholder="Why this track fits the label, release idea, previous work..."
                className="mt-2 bg-zinc-950 border-zinc-800 min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="demo-submit-btn"
              onClick={submit}
              className="btn-neon rounded-full font-display"
            >
              <PaperPlaneTilt size={14} className="mr-2" weight="fill" />
              Send · {estimatedCost} Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResponsePill({ status }) {
  const map = {
    pending: { icon: Clock, cls: 'text-zinc-500' },
    passed: { icon: XCircle, cls: 'text-zinc-500' },
    interested: { icon: CheckCircle, cls: 'text-[#ccff00]' },
    signed: { icon: CheckCircle, cls: 'text-[#ccff00]' },
  };
  const { icon: Icon, cls } = map[status] || map.pending;
  return <span className={`flex items-center gap-1 text-[10px] font-mono uppercase ${cls}`}><Icon size={10} /> {status}</span>;
}

function formatTaxonomyLabel(value) {
  const special = {
    'afro': 'Afro',
    'hip-hop': 'Hip-Hop',
    'rnb-soul': 'R&B / Soul',
    'reggae-dancehall': 'Reggae / Dancehall',
    'soundtrack-cinematic': 'Soundtrack / Cinematic',
    'drum-and-bass': 'Drum & Bass',
    'liquid-dnb': 'Liquid DnB',
    'atmospheric-dnb': 'Atmospheric DnB',
    'minimal-dnb': 'Minimal DnB',
    'dancefloor-dnb': 'Dancefloor DnB',
    'tech-house': 'Tech House',
    'afro-house': 'Afro House',
    'melodic-house': 'Melodic House',
    'melodic-techno': 'Melodic Techno',
    'melodic-dubstep': 'Melodic Dubstep',
    'deep-house': 'Deep House',
    'deep-dubstep': 'Deep Dubstep',
    'uk-garage': 'UK Garage',
    '2-step': '2-Step',
    'acid-house': 'Acid House',
    'acid-techno': 'Acid Techno',
    'idm': 'IDM',
    'nu-skool-breaks': 'Nu-Skool Breaks',
    'j-pop': 'J-Pop',
    'k-pop': 'K-Pop',
    'rnb': 'R&B',
    'pbrnb': 'PBR&B',
    'neo-soul': 'Neo-Soul',
    'alt-pop': 'Alt-Pop',
    'alt-rock': 'Alt-Rock',
  };

  if (special[value]) return special[value];

  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function SearchableFilter({
  label,
  value,
  options,
  placeholder,
  disabled = false,
  onChange,
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel = value === 'all'
    ? placeholder
    : options.find(o => o.value === value)?.label || placeholder;

  return (
    <div className="w-full lg:w-64">
      <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`mt-2 w-full h-10 px-3 rounded-md border border-zinc-800 bg-zinc-950 flex items-center justify-between text-sm text-left ${
              disabled
                ? 'opacity-40 cursor-not-allowed'
                : 'hover:border-zinc-600'
            }`}
          >
            <span className={value === 'all' ? 'text-zinc-400' : 'text-zinc-200'}>
              {selectedLabel}
            </span>
            <span className="text-zinc-600">⌄</span>
          </button>
        </PopoverTrigger>

        {!disabled && (
          <PopoverContent
            align="start"
            className="p-0 w-[var(--radix-popover-trigger-width)] bg-zinc-950 border-zinc-800"
          >
            <Command className="bg-zinc-950">
              <CommandInput
                placeholder={`Search ${label.toLowerCase()}...`}
                className="border-none"
              />

              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>

                <CommandItem
                  value="all"
                  onSelect={() => {
                    onChange('all');
                    setOpen(false);
                  }}
                >
                  {placeholder}
                </CommandItem>

                {options.map(option => (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
